import {
  apiAllowed,
  matchEndpoint,
  type ApiErrorBody,
  type ErrorCodeValue,
  type RoleLike,
} from '@construction/shared';
import { clearTokens, getTokens, setTokens } from './tokens';

// 网页端镜像里是相对的 /api/v1（CR-058：镜像不绑域名），拼地址时要以当前页面为基准才解析得了；
// App 里烧进去的是完整地址，基准会被忽略
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api/v1';

/**
 * 服务器上非接口资源的地址（CR-067：App 内检查更新要读 `/downloads/latest.json`）。
 *
 * 网页端镜像里 `BASE_URL` 是相对的 `/api/v1`，去掉它就是站点根；
 * App 里烧的是完整地址，去掉 `/api/v1` 就是服务器地址。两种情况都别再各自去读环境变量。
 */
export function serverUrl(path: string): string {
  const root = BASE_URL.replace(/\/api\/v1\/?$/, '');
  return `${root}${path}`;
}

/**
 * 带错误码的异常。UI 层只认 code，文案交给 i18n 翻译，
 * 这样中英双语才能覆盖到错误提示，而不是只翻界面按钮。
 */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCodeValue | 'network' | 'unknown',
    readonly status: number,
    readonly params?: Record<string, string | number>,
    readonly traceId?: string,
  ) {
    super(`${code} (${status})`);
    this.name = 'ApiError';
  }

  /** 需要用户重新登录的错误，路由守卫据此跳回登录页 */
  get isAuthFailure(): boolean {
    return (
      this.code === 'TOKEN_EXPIRED' ||
      this.code === 'TOKEN_INVALID' ||
      this.code === 'REFRESH_TOKEN_REVOKED'
    );
  }
}

/** 收到 401 时跳登录页用的回调，由 App 注册，避免这里直接依赖路由 */
let onAuthFailure: (() => void) | null = null;
export function setAuthFailureHandler(handler: () => void): void {
  onAuthFailure = handler;
}

/**
 * 开发环境：页面调了当前角色没权限的接口就在控制台报出来（CR-040）。
 *
 * 路由表的穷举测试查的是「声明的」接口；这一层查「实际调的」。
 * 浏览器走查时搜 `[unauthorized-call]`，出现一条就是某个查询或按钮没按权限收起来。
 * 生产包里整段不跑。由 App 注册，免得这里直接依赖 store（store 反过来依赖这里）。
 */
let roleProvider: (() => RoleLike | null) | null = null;
export function setRoleProvider(provider: () => RoleLike | null): void {
  roleProvider = provider;
}

function reportUnauthorizedCall(method: string, path: string): void {
  if (!import.meta.env.DEV || !roleProvider) return;
  const role = roleProvider();
  if (!role) return;
  const key = matchEndpoint(method, path);
  if (key && !apiAllowed(role, key)) console.error(`[unauthorized-call] ${key}`);
}

/** 同时发出的多个请求一起收到 401 时，只刷新一次 token */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens) return false;

  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });
    if (!response.ok) return false;

    const data = (await response.json()) as { accessToken: string; refreshToken: string };
    setTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return true;
  } catch {
    return false;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** 内部用，防止刷新失败后无限重试 */
  _retried?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query } = options;
  if (!options._retried) reportUnauthorizedCall(method, path);

  const url = new URL(`${BASE_URL}${path}`, globalThis.location?.href);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const tokens = getTokens();
  if (tokens) headers.Authorization = `Bearer ${tokens.accessToken}`;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // fetch 只在网络层失败时抛异常，这里一定是连不上服务器
    throw new ApiError('network', 0);
  }

  // access token 过期：静默刷新一次再重试，工人不该因此被踢回登录页
  if (response.status === 401 && !options._retried && getTokens()) {
    refreshInFlight ??= refreshTokens().finally(() => {
      refreshInFlight = null;
    });
    const refreshed = await refreshInFlight;

    if (refreshed) {
      return request<T>(path, { ...options, _retried: true });
    }
    clearTokens();
    onAuthFailure?.();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const errorBody = payload as ApiErrorBody | null;
    const error = new ApiError(
      errorBody?.code ?? 'unknown',
      response.status,
      errorBody?.params,
      errorBody?.traceId,
    );
    if (error.isAuthFailure) {
      clearTokens();
      onAuthFailure?.();
    }
    throw error;
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * 下载一个文件（CR-043）：带登录态、401 时和普通请求一样刷新一次，
 * 文件名从 `Content-Disposition` 的 `filename*=UTF-8''…` 里解析（中文文件名走这条），
 * 解析不到退回 `filename=`，再退回 `download`。
 *
 * 跨域时（开发环境 5173 调 3000、App 的 WebView）浏览器只在服务端 CORS 放行后才让读这个头
 * （`apps/api/src/common/cors.ts` 的 `exposedHeaders`），否则这里拿到 null，名字一律成了 `download`。
 */
export async function download(
  path: string,
  query?: RequestOptions['query'],
  _retried = false,
): Promise<{ blob: Blob; filename: string }> {
  if (!_retried) reportUnauthorizedCall('GET', path);

  const url = new URL(`${BASE_URL}${path}`, globalThis.location?.href);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  const tokens = getTokens();
  let response: Response;
  try {
    response = await fetch(url.toString(), {
      headers: tokens ? { Authorization: `Bearer ${tokens.accessToken}` } : {},
    });
  } catch {
    throw new ApiError('network', 0);
  }

  if (response.status === 401 && !_retried && getTokens()) {
    refreshInFlight ??= refreshTokens().finally(() => {
      refreshInFlight = null;
    });
    if (await refreshInFlight) return download(path, query, true);
    clearTokens();
    onAuthFailure?.();
  }

  if (!response.ok) {
    const errorBody = safeJson(await response.text()) as ApiErrorBody | null;
    const error = new ApiError(errorBody?.code ?? 'unknown', response.status, errorBody?.params, errorBody?.traceId);
    if (error.isAuthFailure) {
      clearTokens();
      onAuthFailure?.();
    }
    throw error;
  }

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition')) ?? 'download',
  };
}

/** `attachment; filename="x.zip"; filename*=UTF-8''%E6%97%A5...` → 解码后的文件名 */
export function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* 编码坏了就退回普通的 filename */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  download,
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
